import { v4 as uuidv4 } from "uuid";
import { safeLocalStorage } from "../../utils/safeStorage";

const STORAGE_KEY = "valueMetrics:activitySession:v1";
const PROPOSAL_KEY_PREFIX = `${STORAGE_KEY}:proposal:`;
const LOCK_NAME = `${STORAGE_KEY}:lock`;
export const ACTIVITY_SESSION_INACTIVITY_MS = 30 * 60 * 1000;
export const ACTIVITY_SESSION_ARBITRATION_MS = 12;
export const ACTIVITY_SESSION_CLOCK_SKEW_MS = 60 * 1000;
export const ACTIVITY_SESSION_PROPOSAL_TTL_MS = 5 * 60 * 1000;

const safeParse = (value = "") => {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const validState = (value) =>
  value &&
  /^[a-zA-Z0-9_-]{8,160}$/.test(String(value.activitySessionId || "")) &&
  Number.isFinite(Number(value.startedAtMs)) &&
  Number.isFinite(Number(value.lastQualifyingActivityAtMs))
    ? {
        activitySessionId: String(value.activitySessionId),
        ownerId: /^[a-zA-Z0-9_-]{8,160}$/.test(
          String(value.ownerId || ""),
        )
          ? String(value.ownerId)
          : "",
        startedAtMs: Number(value.startedAtMs),
        lastQualifyingActivityAtMs: Number(value.lastQualifyingActivityAtMs),
      }
    : null;

const readState = ({ storage = safeLocalStorage } = {}) =>
  validState(safeParse(storage.getItem(STORAGE_KEY) || ""));

const writeState = (state, { storage = safeLocalStorage } = {}) => {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Activity-session continuity is best effort when storage is unavailable.
  }
  return state;
};

const normalizeOwnerId = (value = "") => {
  const ownerId = String(value || "").trim();
  return /^[a-zA-Z0-9_-]{8,160}$/.test(ownerId) ? ownerId : "";
};

const isExpired = (state, eventTimeMs, currentWallClockMs, ownerId = "") =>
  !state ||
  (ownerId && state.ownerId !== ownerId) ||
  state.lastQualifyingActivityAtMs - currentWallClockMs >
    ACTIVITY_SESSION_CLOCK_SKEW_MS ||
  (eventTimeMs >= state.lastQualifyingActivityAtMs &&
    eventTimeMs - state.lastQualifyingActivityAtMs >=
      ACTIVITY_SESSION_INACTIVITY_MS);

const resolveWallClockMs = (currentWallClockMs) => {
  const value =
    typeof currentWallClockMs === "function"
      ? currentWallClockMs()
      : currentWallClockMs;
  const wallClockMs = Number(value);
  if (!Number.isFinite(wallClockMs)) {
    throw new Error("activity-session wall-clock timestamp must be finite");
  }
  return wallClockMs;
};

const isValidActivitySessionId = (activitySessionId) =>
  /^[a-zA-Z0-9_-]{8,160}$/.test(activitySessionId);

const validateActivitySessionId = (activitySessionId) => {
  if (!isValidActivitySessionId(activitySessionId)) {
    throw new Error("activitySessionId generator returned an invalid id");
  }
  return activitySessionId;
};

const sessionResult = (
  state,
  { rotated = false, extended = false } = {},
) => ({
  ...state,
  rotated,
  extended,
  inactivityMs: ACTIVITY_SESSION_INACTIVITY_MS,
});

const defaultDelay = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

const browserLockManager = () => {
  try {
    return typeof navigator !== "undefined" &&
      navigator.locks &&
      typeof navigator.locks.request === "function"
      ? navigator.locks
      : null;
  } catch {
    return null;
  }
};

const isUnavailableLockManagerError = (error) =>
  ["SecurityError", "NotSupportedError", "InvalidStateError"].includes(
    String(error?.name || ""),
  );

const listConcurrentProposals = ({
  storage,
  observedActivitySessionId,
  observedLastQualifyingActivityAtMs,
  observedOwnerId,
  ownerId,
  nowMs,
  currentWallClockMs,
}) => {
  const proposals = [];
  const staleKeys = [];
  try {
    for (let index = 0; index < Number(storage.length || 0); index += 1) {
      const key = storage.key(index);
      if (!key || !key.startsWith(PROPOSAL_KEY_PREFIX)) continue;
      const proposal = safeParse(storage.getItem(key) || "");
      const activitySessionId = String(
        proposal?.activitySessionId || "",
      );
      const proposedAtMs = Number(proposal?.proposedAtMs);
      const createdAtWallClockMs = Number(
        proposal?.createdAtWallClockMs,
      );
      if (
        !proposal ||
        !isValidActivitySessionId(activitySessionId) ||
        !Number.isFinite(proposedAtMs) ||
        !Number.isFinite(createdAtWallClockMs) ||
        (ownerId && proposal?.ownerId !== ownerId) ||
        createdAtWallClockMs - currentWallClockMs >
          ACTIVITY_SESSION_CLOCK_SKEW_MS ||
        currentWallClockMs - createdAtWallClockMs >
          ACTIVITY_SESSION_PROPOSAL_TTL_MS
      ) {
        staleKeys.push(key);
        continue;
      }
      if (
        proposal.observedActivitySessionId !== observedActivitySessionId ||
        String(proposal.observedOwnerId || "") !== observedOwnerId ||
        Number(proposal.observedLastQualifyingActivityAtMs) !==
          observedLastQualifyingActivityAtMs ||
        Math.abs(Number(proposal.proposedAtMs) - nowMs) >
          ACTIVITY_SESSION_ARBITRATION_MS * 10
      ) {
        continue;
      }
      proposals.push({
        activitySessionId,
        proposedAtMs,
      });
    }
    staleKeys.forEach((key) => storage.removeItem(key));
  } catch {
    return [];
  }
  return proposals;
};

const emptyActivitySession = () => ({
  activitySessionId: null,
  startedAtMs: null,
  lastQualifyingActivityAtMs: null,
  rotated: false,
  extended: false,
  inactivityMs: ACTIVITY_SESSION_INACTIVITY_MS,
});

export const getActivitySession = ({
  occurredAtMs = Date.now(),
  qualifyingActivity = true,
  activitySessionOwnerId = "",
  createId = uuidv4,
  storage = safeLocalStorage,
  currentWallClockMs = Date.now,
} = {}) => {
  const nowMs = Number(occurredAtMs);
  const wallClockMs = resolveWallClockMs(currentWallClockMs);
  const ownerId = normalizeOwnerId(activitySessionOwnerId);
  if (!Number.isFinite(nowMs)) {
    throw new Error("activity-session event timestamp must be finite");
  }
  const existing = readState({ storage });
  if (qualifyingActivity !== true) {
    return existing && (!ownerId || existing.ownerId === ownerId)
      ? {
          ...existing,
          rotated: false,
          extended: false,
          inactivityMs: ACTIVITY_SESSION_INACTIVITY_MS,
        }
      : emptyActivitySession();
  }
  const expired = isExpired(existing, nowMs, wallClockMs, ownerId);
  const state = {
    activitySessionId: expired
      ? String(createId())
      : existing.activitySessionId,
    startedAtMs: expired ? nowMs : existing.startedAtMs,
    ownerId: ownerId || existing?.ownerId || "",
    lastQualifyingActivityAtMs: expired
      ? nowMs
      : Math.max(nowMs, existing.lastQualifyingActivityAtMs),
  };
  validateActivitySessionId(state.activitySessionId);
  writeState(state, { storage });
  return sessionResult(state, {
    rotated: expired,
    extended: !expired,
  });
};

const getStorageArbitratedActivitySession = async ({
  occurredAtMs,
  createId,
  storage,
  delay,
  currentWallClockMs,
  activitySessionOwnerId,
}) => {
  const nowMs = Number(occurredAtMs);
  const wallClockMs = resolveWallClockMs(currentWallClockMs);
  const ownerId = normalizeOwnerId(activitySessionOwnerId);
  const existing = readState({ storage });
  if (!isExpired(existing, nowMs, wallClockMs, ownerId)) {
    return getActivitySession({
      occurredAtMs: nowMs,
      qualifyingActivity: true,
      createId,
      storage,
      currentWallClockMs: wallClockMs,
      activitySessionOwnerId: ownerId,
    });
  }

  const activitySessionId = validateActivitySessionId(String(createId()));
  const observedActivitySessionId = existing?.activitySessionId || "";
  const observedLastQualifyingActivityAtMs =
    existing?.lastQualifyingActivityAtMs || 0;
  const observedOwnerId = existing?.ownerId || "";
  const proposalKey = `${PROPOSAL_KEY_PREFIX}${activitySessionId}`;
  try {
    storage.setItem(
      proposalKey,
      JSON.stringify({
        activitySessionId,
        proposedAtMs: nowMs,
        createdAtWallClockMs: wallClockMs,
        observedActivitySessionId,
        observedLastQualifyingActivityAtMs,
        observedOwnerId,
        ownerId,
      }),
    );
  } catch {
    // Continue with the generated candidate when proposal storage is absent.
  }

  await delay(ACTIVITY_SESSION_ARBITRATION_MS);
  try {
    const canonical = readState({ storage });
    const arbitrationWallClockMs =
      resolveWallClockMs(currentWallClockMs);
    const proposals = listConcurrentProposals({
      storage,
      observedActivitySessionId,
      observedLastQualifyingActivityAtMs,
      observedOwnerId,
      ownerId,
      nowMs,
      currentWallClockMs: arbitrationWallClockMs,
    });
    if (!isExpired(canonical, nowMs, arbitrationWallClockMs, ownerId)) {
      const state = {
        ...canonical,
        lastQualifyingActivityAtMs: Math.max(
          canonical.lastQualifyingActivityAtMs,
          nowMs,
        ),
      };
      writeState(state, { storage });
      return sessionResult(state, {
        rotated:
          canonical.activitySessionId !== observedActivitySessionId,
        extended:
          canonical.activitySessionId === observedActivitySessionId,
      });
    }

    const winner =
      proposals.sort(
        (left, right) =>
          left.activitySessionId.localeCompare(right.activitySessionId) ||
          left.proposedAtMs - right.proposedAtMs,
      )[0] || { activitySessionId, proposedAtMs: nowMs };
    const proposalTimes = proposals
      .map((proposal) => proposal.proposedAtMs)
      .filter(Number.isFinite);
    const state = {
      activitySessionId: winner.activitySessionId,
      ownerId,
      startedAtMs: Math.min(nowMs, ...proposalTimes),
      lastQualifyingActivityAtMs: Math.max(nowMs, ...proposalTimes),
    };
    writeState(state, { storage });
    return sessionResult(state, { rotated: true, extended: false });
  } finally {
    try {
      storage.removeItem(proposalKey);
    } catch {
      // Proposal cleanup is best effort.
    }
  }
};

export const getCoordinatedActivitySession = async ({
  occurredAtMs = Date.now(),
  qualifyingActivity = true,
  activitySessionOwnerId = "",
  createId = uuidv4,
  storage = safeLocalStorage,
  lockManager = browserLockManager(),
  delay = defaultDelay,
  currentWallClockMs = Date.now,
} = {}) => {
  const nowMs = Number(occurredAtMs);
  if (!Number.isFinite(nowMs)) {
    throw new Error("activity-session event timestamp must be finite");
  }
  if (qualifyingActivity !== true) {
    return getActivitySession({
      occurredAtMs: nowMs,
      qualifyingActivity: false,
      createId,
      storage,
      currentWallClockMs,
      activitySessionOwnerId,
    });
  }
  if (lockManager && typeof lockManager.request === "function") {
    try {
      return await lockManager.request(
        LOCK_NAME,
        { mode: "exclusive" },
        () =>
          getActivitySession({
            occurredAtMs: nowMs,
            qualifyingActivity: true,
            createId,
            storage,
            currentWallClockMs,
            activitySessionOwnerId,
          }),
      );
    } catch (error) {
      if (!isUnavailableLockManagerError(error)) throw error;
      // Web Locks can be exposed but forbidden in embedded or restricted
      // browser contexts. The existing storage arbitration is the canonical
      // fallback and must not turn an optional coordination API into a blocker.
    }
  }
  return getStorageArbitratedActivitySession({
    occurredAtMs: nowMs,
    createId,
    storage,
    delay,
    currentWallClockMs,
    activitySessionOwnerId,
  });
};

export const clearActivitySessionForTests = ({
  storage = safeLocalStorage,
} = {}) => {
  try {
    storage.removeItem(STORAGE_KEY);
    const proposalKeys = [];
    for (let index = 0; index < Number(storage.length || 0); index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(PROPOSAL_KEY_PREFIX)) proposalKeys.push(key);
    }
    proposalKeys.forEach((key) => storage.removeItem(key));
  } catch {
    // Test cleanup is best effort for storage shims.
  }
};

export const _private = {
  STORAGE_KEY,
  PROPOSAL_KEY_PREFIX,
  getStorageArbitratedActivitySession,
  listConcurrentProposals,
  readState,
  resolveWallClockMs,
  normalizeOwnerId,
  isUnavailableLockManagerError,
  validState,
  writeState,
};
